/**
 * Lines of a value axis that counts people: whole numbers, round steps, few of them.
 *
 * The step is the smallest of 1, 2 and 5 times a power of ten that covers `min`..`max` in at
 * most four steps, so the axis has five lines at most, zero included, and the outer lines are
 * the first round numbers at or beyond the tallest and the deepest bar. A change can go below
 * zero, a count cannot, so `min` is zero unless told otherwise. An empty window still gets a
 * 0..1 axis - a flat line with nothing to measure it against reads as a broken chart.
 */
export function getNiceTicks(max: number, min = 0): number[] {
  // a number that is not one - a field an older backend does not send yet - would never fit
  // in four steps, and the loop below would never end
  if (!Number.isFinite(max) || !Number.isFinite(min) || (max <= 0 && min >= 0)) {
    return [0, 1];
  }

  for (let power = 1; ; power *= 10) {
    for (const multiple of [1, 2, 5]) {
      const step = multiple * power;
      const low = Math.floor(min / step);
      const steps = Math.ceil(max / step) - low;
      if (steps <= 4) {
        // `+ 0` turns the -0 of an axis that ends at zero from below into a plain 0
        return Array.from({ length: steps + 1 }, (_, index) => (low + index) * step + 0);
      }
    }
  }
}

/**
 * A number of people the way the screen writes it: "1 234", and "−3" with a real minus.
 *
 * The russian locale writes a hyphen for the minus, which is shorter than the digits and looks
 * like a dash next to a range of hours.
 */
export function formatCount(value: number): string {
  return value < 0 ? `−${(-value).toLocaleString("ru-RU")}` : value.toLocaleString("ru-RU");
}
